import React from "react";
import { AlertCircle, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * "The brand you were looking at is gone." B6.
 *
 * The brand-level analogue of the workspace-reseat notice, and it exists for
 * the argument `backend/auth.py` makes at length about that one: healing
 * silently is its own defect. A brand deleted in another tab (or by
 * `/brands/reset`, or by a teammate) leaves this tab reading a 404. The two
 * tempting responses are both wrong —
 *
 *   · keep going: a screen of a deleted brand's numbers, refreshing forever;
 *   · re-derive quietly: the heading changes, every number changes, nothing
 *     says why, and the user concludes the app lost their data.
 *
 * So we re-derive AND say it. The banner is dismissible because it describes a
 * finished event, not an ongoing fault — there is nothing to retry and nothing
 * to wait for, and an undismissable bar about something already resolved is
 * just furniture.
 *
 * It sits next to WorkspaceBanner rather than inside it: that component owns
 * "we cannot reach the workspace", which is a live, self-healing fault with a
 * retry ladder behind it. Same slot, different lifetime, different message.
 *
 * COPY SLOT brand.gone.* — ORI, PLEASE REVIEW. Serviceable, not settled. The
 * hard part is that the user did not do this and probably cannot undo it: the
 * line has to explain a disappearance without implying blame or data loss, and
 * "deleted" is a frightening word for something that may have been a teammate
 * tidying up. The named and unnamed variants must both read naturally.
 */
export default function BrandGoneBanner() {
  const { brandGone, clearBrandGone, brand } = useAuth();
  if (!brandGone) return null;

  const name = brandGone.name;

  return (
    <div
      role="status"
      data-testid="brand-gone-banner"
      className="sticky top-16 z-[9] w-full border-b border-amber-200 bg-amber-50 px-4 md:px-8 py-2.5"
    >
      <div className="max-w-[1400px] mx-auto flex items-start gap-2.5">
        <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" strokeWidth={2.2} />
        <p className="flex-1 text-sm text-amber-900 leading-relaxed">
          {name ? (
            <>
              <span className="font-semibold">{name}</span> isn't in this workspace any more.
            </>
          ) : (
            <>That brand isn't in this workspace any more.</>
          )}{" "}
          {brand?.name ? (
            <>
              You're now looking at <span className="font-semibold">{brand.name}</span>.
            </>
          ) : (
            <>This workspace has no brands left.</>
          )}
        </p>
        <button
          type="button"
          onClick={clearBrandGone}
          data-testid="brand-gone-dismiss"
          aria-label="Dismiss"
          className="shrink-0 -mr-1 p-1 rounded text-amber-700 hover:bg-amber-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
